import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

/**
 * MaintenanceOverlay
 * Polls GET /api/maintenance-status every 60s. When the backend reports
 * `maintenance: true`, replaces the entire app with a full-screen maintenance
 * page. Founders bypass the overlay — they continue to see the normal app so
 * they can disable maintenance from the Founder Dashboard.
 */
export default function MaintenanceOverlay({ children }) {
  const { user } = useAuth();
  const [maintenance, setMaintenance] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/maintenance-status`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setMaintenance(!!data.maintenance);
        setMessage(data.message || "");
      } catch {
        /* network blip — keep the previous state to avoid flapping */
      }
    };

    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Founder bypass — must be logged in AND flagged as founder.
  const isFounder = !!(user && user.is_founder === true);

  if (maintenance && !isFounder) {
    return <MaintenancePage message={message} />;
  }

  return children;
}

const BG = "#06080F";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#9CA3AF";
const TEXT_DIM = "#6B7280";

function MaintenancePage({ message }) {
  return (
    <div
      data-testid="maintenance-page"
      style={{
        minHeight: "100vh",
        background: BG,
        color: TEXT_PRIMARY,
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
      `}</style>

      <img
        src="/inference-logo.svg"
        alt="Inference"
        data-testid="maintenance-logo"
        style={{ height: 100, width: "auto", display: "block", marginBottom: 36 }}
      />

      <h1
        data-testid="maintenance-headline"
        style={{
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          marginBottom: 14,
          textAlign: "center",
        }}
      >
        We'll be right back
      </h1>

      <p
        data-testid="maintenance-message"
        style={{
          fontSize: 15,
          color: TEXT_MUTED,
          lineHeight: 1.6,
          maxWidth: 540,
          textAlign: "center",
          marginBottom: 8,
        }}
      >
        {message || "Inference is temporarily down for maintenance. We will be back shortly."}
      </p>

      <p
        data-testid="maintenance-footnote"
        style={{
          fontSize: 12.5,
          color: TEXT_DIM,
          marginTop: 32,
          fontFamily: "'DM Mono', monospace",
          letterSpacing: "0.03em",
        }}
      >
        Follow @inferenceapp for updates
      </p>
    </div>
  );
}
